import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
	OutlinedTextBlackButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import { TextArea } from "src/components/Default/LekUI/TextArea";
import { useCreateAiResponseReview } from "src/hooks/useAi";
import * as Yup from "yup";

interface AiResponseReviewFormProps {
	responseId: string | number;
	onSubmitted: () => void;
	onCancel: () => void;
}

export const AiResponseReviewForm = ({
	responseId,
	onSubmitted,
	onCancel,
}: AiResponseReviewFormProps) => {
	const { t } = useTranslation();
	const { mutateAsync: createReview, isPending: isSubmitting } =
		useCreateAiResponseReview();

	const form = useFormik({
		initialValues: { review: "" },
		validationSchema: Yup.object().shape({
			review: Yup.string().trim().required(),
		}),
		onSubmit: async (values, { resetForm }) => {
			try {
				await createReview({
					id: Number(responseId),
					review: values.review.trim(),
				});
				toast.success(t("ai.chatbot.reviewSent", "Dziękujemy za uwagę"), {
					position: toast.POSITION.BOTTOM_LEFT,
					autoClose: 3000,
				});
				resetForm();
				onSubmitted();
			} catch {
				toast.error(t("errors.somethingWentWrong"), {
					position: toast.POSITION.BOTTOM_LEFT,
					autoClose: 5000,
				});
			}
		},
	});

	const handleCancel = () => {
		form.resetForm();
		onCancel();
	};

	return (
		<form onSubmit={form.handleSubmit} className="flex flex-col gap-8 w-100">
			<TextArea
				name="review"
				variant="outlined"
				fullWidth
				multiline
				rows={4}
				placeholder={t(
					"ai.chatbot.reviewPlaceholder",
					"Co możemy poprawić lub czego brakuje w tym omówieniu",
				)}
				value={form.values.review}
				onChange={form.handleChange}
				disabled={isSubmitting}
			/>
			<div className="flex items-center align-self-end gap-8">
				<OutlinedTextBlackButton
					className="size-16-light"
					onClick={handleCancel}
					disabled={isSubmitting}
				>
					{t("commands.cancel", "Anuluj")}
				</OutlinedTextBlackButton>
				<PrimaryButton
					className="size-16-light"
					type="submit"
					isLoading={isSubmitting}
					disabled={!form.values.review.trim()}
				>
					{t("commands.send", "Wyślij")}
				</PrimaryButton>
			</div>
		</form>
	);
};
